import { UserService } from '@/services/user'
import type { IUser } from '@/types/user'
import { useQueries, type UseQueryOptions } from '@tanstack/react-query'

type UseUsersProps = {
  userIds: string[]
  enabled?: boolean
  options?: Partial<
    UseQueryOptions<IUser, Error, IUser, readonly ['user', string]>
  >
}

export const useUsers = ({
  userIds,
  enabled = true,
  options = {},
}: UseUsersProps) => {
  const results = useQueries({
    queries: userIds.map((userId) => ({
      queryKey: ['user', userId] as const,
      queryFn: async (): Promise<IUser> => {
        const response = await UserService.getUserById(userId)
        return response.data
      },
      enabled: enabled && userIds.length > 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1, // Only retry once to avoid infinite loops
      ...options, // Allow overriding default options
    })),
  })

  // Extract data and handle loading/error states
  const users = results
    .map((result) => result.data)
    .filter((user): user is IUser => user !== undefined)

  const isLoading = results.some((result) => result.isLoading)
  const isError = results.some((result) => result.isError)
  const errors = results
    .map((result) => result.error)
    .filter((error) => error !== null)

  return {
    users,
    isLoading,
    isError,
    errors,
  }
}
